// src/components/NotificationPrompt.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import notificationService from '../services/notificationService';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../contexts/ThemeContext';

const NotificationPrompt = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useThemeSettings();

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        return;
      }

      // If user still has to login, don't show the prompt (if user is on /login page)
      if (window.location.pathname === '/login') {
        return;
      }

      // Check if already granted
      if (Notification.permission === 'granted') {
        // Initialize and subscribe
        const initialized = await notificationService.initialize();
        if (initialized) {
          const subscribed = await notificationService.isSubscribed();
          if (!subscribed) {
            // Show prompt to subscribe
            setOpen(true);
          }
        }
        return;
      }

      // Check if permission is default (not asked yet)
      if (Notification.permission === 'default') {
        // Show prompt after a delay (don't be too aggressive)
        setTimeout(() => {
          setOpen(true);
        }, 5000);
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    }
  };

  const handleEnable = async () => {
    try {
      setLoading(true);

      // Initialize notification service
      const initialized = await notificationService.initialize();
      if (!initialized) {
        throw new Error('Failed to initialize notification service');
      }

      // Subscribe to push notifications
      const subscribed = await notificationService.subscribe();
      if (subscribed) {
        enqueueSnackbar(t ? t('Notifications.enabled_success') : 'Push notifications enabled successfully!', { variant: 'success' });
        setOpen(false);
      } else {
        throw new Error('Failed to subscribe to push notifications');
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      enqueueSnackbar(t ? t('Notifications.enable_failed') : 'Failed to enable push notifications. Please check your browser settings.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLater = () => {
    setOpen(false);
    // Show again after 24 hours
    localStorage.setItem('notificationPromptDismissed', Date.now().toString());
  };

  const handleNever = () => {
    setOpen(false);
    localStorage.setItem('notificationPromptNever', 'true');
  };

  return (
    <Dialog
      open={open}
      onClose={handleLater}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveIcon color="primary" />
          <Typography variant="h6">{t ? t('Notifications.prompt_title') : 'Enable Push Notifications'}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t ? t('Notifications.prompt_description') : 'Stay up-to-date with real-time notifications about:'}
        </DialogContentText>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_tickets') : 'Ticket updates and assignments'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_devices') : 'Device status changes'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_files') : 'File sharing and uploads'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {t ? t('Notifications.feature_alerts') : 'Critical system alerts'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          {t ? t('Notifications.critical_sound_info') : 'Critical alerts will play a sound even when your device is muted, ensuring you never miss important notifications.'}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleNever} color="inherit">
          {t ? t('Notifications.button_never') : 'Never'}
        </Button>
        <Button onClick={handleLater} color="inherit">
          {t ? t('Notifications.button_later') : 'Later'}
        </Button>
        <Button
          onClick={handleEnable}
          variant="contained"
          disabled={loading}
        >
          {loading ? (t ? t('Notifications.button_enabling') : 'Enabling...') : (t ? t('Notifications.button_enable') : 'Enable Notifications')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPrompt;
